import React, { FC, useEffect } from 'react';
import Lottie from 'react-lottie';
import { useToast } from '@common/toast';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { keys as verificationCheckKeys, useSaveKcbIdentity } from '@repository/identity/useIdentity';
import certification from '@graphic/certification.json';
import KcbSmsVerificationRs from '@domain/rs/verification/KcbSmsVerificationRs';
import KcbIpinVerificationRs from '@domain/rs/verification/KcbIpinVerificationRs';
import { V2Route } from '@utils/routeUtil';
import { baseURL } from '@utils/common';
import { queryClient } from '@utils/queryClient';

import classnames from 'classnames/bind';
import styles from './certification.module.scss';
const cx = classnames.bind(styles);

const options = {
  loop: true,
  autoplay: true,
  animationData: certification,
};

interface CertificationProps {
  onLoading: () => void;
  onCloseModal: () => void;
}

const Certification: FC<CertificationProps> = ({ onLoading, onCloseModal }) => {
  const { data: userInfo } = useUserInfo();
  const userName = userInfo?.name ?? '회원';
  const Toast = useToast();

  const { mutateAsync: saveKcbIdentityMutateAsync } = useSaveKcbIdentity((err) => {
    const errMessage = err.response?.data.message ?? '본인인증 실패하였습니다.';

    onCloseModal();
    Toast({ type: 'error', iconType: 'info', content: errMessage });
    return;
  });

  const handleSMS = () => {
    V2Route.openKcbSmsPage(onCloseModal);
    onLoading();
  };

  const handleIPIN = () => {
    V2Route.openKcbIpinPage(onCloseModal);
    onLoading();
  };

  useEffect(() => {
    //본인 인증 완료시 이벤트 리스너 등록
    window.addEventListener('message', handleVerificationEvent, false);
    return () => window.removeEventListener('message', handleVerificationEvent);
  }, []);

  //본인인증 관련 child window 에서 넘어오는 완료 상태값 처리
  const handleVerificationEvent = async (event: MessageEvent) => {
    try {
      // 신뢰할 수 없는 origin 처리
      if (baseURL !== event.origin) return;

      const verificationRs: KcbSmsVerificationRs | KcbIpinVerificationRs = JSON.parse(event.data);
      //ipin 인증 성공 : T000, sms 인증 성공 : B000
      if (!['T000', 'B000'].includes(verificationRs.code)) {
        onCloseModal();
        Toast({ type: 'error', iconType: 'info', content: '본인인증 실패하였습니다.' });
        return;
      }

      const result = await saveKcbIdentityMutateAsync(verificationRs);

      if (result !== true) {
        onCloseModal();
        Toast({ type: 'error', iconType: 'info', content: '본인인증 실패하였습니다.' });
        return;
      }

      //본인인증 여부 갱신
      await queryClient.refetchQueries(verificationCheckKeys, { active: true, exact: true });
      console.log('verification:', result); // DV, STG 환경에서 성공 테스트 확인용으로 남겨둠
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className={cx('certificationArea')}>
      <div className={cx('logo')}>
        <span className={cx('hidden')}>JOBDA</span>
      </div>

      <div className={cx('infoArea')}>
        <div className={cx('lottieWrap')}>
          <Lottie options={options} />
        </div>

        <em className={cx('title')}>
          {userName}님의 본인 확인을 위해
          <br />
          본인 인증이 필요해요.
        </em>

        <p className={cx('desc')}>본인 인증은 최초 1회만 필요해요.</p>
        <div className={cx('btnArea')}>
          <a className={cx('btn', 'iPin')} role="button" onClick={handleIPIN}>
            아이핀 인증
          </a>
          <a className={cx('btn')} role="button" onClick={handleSMS}>
            휴대폰 본인인증
          </a>
        </div>
      </div>
    </div>
  );
};

export default Certification;
